/**
 * Explore Ayrshire Hub — the completeness guarantee.
 *
 * The homepage above this point is deliberately selective: it shows what you
 * probably want right now, and hides sections with nothing in them. That is
 * good for hierarchy and bad for discovery, so everything the platform can do
 * is listed here, once, in full.
 *
 * This is the contract that makes the contextual homepage safe. If a section
 * above is hidden because it is 3am, or because Freecycle currently has no
 * items, its destination is still one tap away from here. Nothing on the
 * platform may be reachable ONLY from a contextual section.
 *
 * Grouped by intent rather than by internal module, because "I need something
 * done" is how people think, and "trades" is not.
 */
import { Link } from "wouter";
import {
  ShoppingBag, Store, Tag, Calendar, Beer, Wrench, Hammer, Briefcase,
  Newspaper, Users, Package, Building2, Trophy, Car, Globe, LifeBuoy,
  MessageCircle, Ticket, Gauge,
} from "lucide-react";
import { RADII, TAP_TARGET, FOCUS_RING } from "@/components/hub";
import { cn } from "@/lib/utils";

interface ExploreItem {
  href: string;
  label: string;
  hint: string;
  icon: React.ReactNode;
}

interface ExploreGroup {
  heading: string;
  items: ExploreItem[];
}

/**
 * Every customer-facing destination, verified against App.tsx routes.
 * Account-scoped pages (/account, /my-orders, /saved, …) live in the header
 * menu rather than here — they are about *your* things, not about Ayrshire.
 */
export const EXPLORE_GROUPS: ExploreGroup[] = [
  {
    heading: "Order & shop",
    items: [
      { href: "/order",       label: "Order food",    hint: "Restaurants & takeaways", icon: <ShoppingBag size={18} /> },
      { href: "/shop-local",  label: "Shop local",    hint: "Groceries & retail",      icon: <Store size={18} /> },
      { href: "/deals",       label: "Deals",         hint: "Local offers",            icon: <Tag size={18} /> },
      { href: "/businesses",  label: "Business directory", hint: "Who's who locally",  icon: <Building2 size={18} /> },
    ],
  },
  {
    heading: "Out and about",
    items: [
      { href: "/events",   label: "What's on",   hint: "Events across Ayrshire", icon: <Calendar size={18} /> },
      { href: "/places",   label: "Places",      hint: "Pubs, clubs & venues",   icon: <Beer size={18} /> },
      { href: "/hub-dash", label: "Hub Dash",    hint: "Getting around",         icon: <Gauge size={18} /> },
      { href: "/competition", label: "Competition", hint: "Win cash",            icon: <Trophy size={18} /> },
    ],
  },
  {
    heading: "Get something done",
    items: [
      { href: "/post-job",  label: "Get quotes",   hint: "Tell trades what you need", icon: <Wrench size={18} /> },
      { href: "/jobs",      label: "Quote requests", hint: "Jobs residents have posted", icon: <Hammer size={18} /> },
      { href: "/vacancies", label: "Find work",    hint: "Local vacancies",          icon: <Briefcase size={18} /> },
    ],
  },
  {
    heading: "Your community",
    items: [
      { href: "/community",   label: "Community",  hint: "Posts & discussion",   icon: <Users size={18} /> },
      { href: "/news",        label: "Local news", hint: "Ayrshire stories",     icon: <Newspaper size={18} /> },
      { href: "/marketplace", label: "Freecycle",  hint: "Free to a good home",  icon: <Package size={18} /> },
      { href: "/help",        label: "Local support", hint: "Helplines & welfare", icon: <LifeBuoy size={18} /> },
    ],
  },
  {
    heading: "Run a business",
    items: [
      { href: "/business",             label: "Business portal",  hint: "Manage your listings",  icon: <Building2 size={18} /> },
      { href: "/submit-business",      label: "List your business", hint: "Free to join",        icon: <Store size={18} /> },
      { href: "/list-your-venue",      label: "List your venue",  hint: "Pubs, clubs & halls",   icon: <Beer size={18} /> },
      { href: "/submit-event",         label: "Submit an event",  hint: "Tell Ayrshire about it", icon: <Ticket size={18} /> },
      { href: "/become-tradesperson",  label: "Join as a trade",  hint: "Get job leads",         icon: <Wrench size={18} /> },
      { href: "/become-a-driver",      label: "Become a driver",  hint: "Deliver locally",       icon: <Car size={18} /> },
      { href: "/free-website",         label: "Free website",     hint: "For local businesses",  icon: <Globe size={18} /> },
      { href: "/support",              label: "Contact support",  hint: "We'll get back to you", icon: <MessageCircle size={18} /> },
    ],
  },
];

export function ExploreGrid({ onNavigate }: { onNavigate?: (href: string, label: string) => void }) {
  return (
    <div className="space-y-6">
      {EXPLORE_GROUPS.map((group) => (
        <div key={group.heading}>
          <h3 className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2.5">
            {group.heading}
          </h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {group.items.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  onClick={() => onNavigate?.(item.href, item.label)}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2.5 bg-white border border-slate-100",
                    "hover:border-slate-300 hover:bg-slate-50 transition-colors",
                    RADII.md, TAP_TARGET, FOCUS_RING,
                  )}
                >
                  <span className="shrink-0 text-[#1e3a5f]">{item.icon}</span>
                  <span className="min-w-0">
                    <span className="block text-[13px] font-semibold text-slate-900 truncate">
                      {item.label}
                    </span>
                    <span className="block text-[11px] text-slate-500 truncate">{item.hint}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}
